import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, Subject, tap, throwError } from 'rxjs';

// Models
import { Errors } from '../../shared/_enums/errors.enum';
import { SearchResponse } from '../../navigation/models/search.model';

@Injectable({
  providedIn: 'root',
})
export class SearchService {

  // Services
  private readonly httpClient = inject(HttpClient);
  private readonly location = inject(Location);

  // Subscriptions
  private readonly searchError: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly searchQuery: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly searchResponse: BehaviorSubject<SearchResponse> = new BehaviorSubject<SearchResponse>(new SearchResponse());
  private readonly searching: Subject<boolean> = new Subject<boolean>();

  /**
   * Any errors returned from a search query.
   * @readonly
   * @type {Observable<string | null>}
   * @memberof SearchService
   */
  readonly searchError$: Observable<string | null> = this.searchError.asObservable();

  /**
   * The previous search query.
   * @readonly
   * @type {Observable<string>}
   * @memberof SearchService
   */
  readonly searchQuery$: Observable<string> = this.searchQuery.asObservable();

  /**
   * The response returned from a search query.
   * @readonly
   * @type {Observable<SearchResponse>}
   * @memberof SearchService
   */
  readonly searchResponse$: Observable<SearchResponse> = this.searchResponse.asObservable();

  /**
   * An indicator of the search operation being performed.
   * @readonly
   * @type {Observable<boolean>}
   * @memberof SearchService
   */
  readonly searching$: Observable<boolean> = this.searching.asObservable();

  /**
   * Performs a search based on the search query provided.
   * @param searchQuery the search query value.
   * @memberof SearchService
   */
  search(searchQuery: string): Observable<SearchResponse> {
    // Set operation state
    this.searching.next(true);

    // Replace URL state and set search query
    this.location.replaceState(`search?q=${encodeURIComponent(searchQuery)}`);
    this.searchQuery.next(searchQuery);

    // Perform search query
    return this.httpClient.post<SearchResponse>('ppf/search', { query: searchQuery })
      .pipe(
        tap((searchResponse: SearchResponse) => {
          this.searchResponse.next(searchResponse);
          this.searchError.next(null);
          this.searching.next(false);
        }),
        catchError((err: HttpErrorResponse) => {
          this.searchResponse.next(new SearchResponse());
          this.searchError.next(Errors[err.error.error as keyof typeof Errors] || 'There was an error retrieving your search results.');
          this.searching.next(false);
          return throwError(() => err.error.error || 'An error occurred while searching.');
        }),
      );
  }
}
